import React, { useMemo } from 'react'
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Skeleton,
  Tooltip,
} from '@mui/material'
import { LightspeedIcon, XeroIcon, SkimmerIcon } from '../../SystemIcons'
import { formatTimeAgo } from '../../../utils/tableFormatters'
import { deriveSyncStatus } from '../../../utils/syncFreshness'

export interface HeroSystemSync {
  last_sync_at?: string | null
}

export interface HeroSystems {
  lightspeed?: HeroSystemSync
  xero?: HeroSystemSync
  skimmer?: HeroSystemSync
}

interface SystemIndicatorRow {
  key: 'lightspeed' | 'xero' | 'skimmer'
  label: string
  icon: React.ReactNode
  connected: boolean
  lastSyncAt?: string | null
}

interface SystemsIndicatorProps {
  rows: SystemIndicatorRow[]
}

const SystemsIndicator: React.FC<SystemsIndicatorProps> = ({ rows }) => {
  if (rows.length === 0) return null
  return (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
      {rows.map((row) => {
        const sync = deriveSyncStatus(row.lastSyncAt)
        const dotColor = row.connected ? sync.dotColor : 'text.disabled'
        const timeLabel = row.connected && row.lastSyncAt ? formatTimeAgo(row.lastSyncAt) : null
        const tooltip = row.connected
          ? row.lastSyncAt
            ? `${row.label} · ${sync.label}`
            : `${row.label} · Connected, no sync yet`
          : `${row.label} · Not connected`
        return (
          <Tooltip key={row.key} title={tooltip} arrow>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ cursor: 'default' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  opacity: row.connected ? 1 : 0.45,
                }}
              >
                {row.icon}
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: row.connected ? 'text.primary' : 'text.disabled' }}>
                {row.label}
              </Typography>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: dotColor,
                  flexShrink: 0,
                }}
              />
              {timeLabel && (
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {timeLabel}
                </Typography>
              )}
            </Stack>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

interface DashboardHeroProps {
  franchiseName: string
  country?: string
  state?: string
  lightspeedStoreCode?: string | null
  xeroShortCode?: string | null
  fieldManagementSystem?: string
  systems?: HeroSystems
  loading?: boolean
}

export const DashboardHero: React.FC<DashboardHeroProps> = ({
  franchiseName,
  country,
  state,
  lightspeedStoreCode,
  xeroShortCode,
  fieldManagementSystem,
  systems,
  loading = false,
}) => {
  const rows = useMemo<SystemIndicatorRow[]>(() => {
    const includeSkimmer = fieldManagementSystem?.toLowerCase() === 'skimmer'
    const out: SystemIndicatorRow[] = [
      {
        key: 'lightspeed',
        label: 'Lightspeed',
        icon: <LightspeedIcon size={16} />,
        connected: Boolean(lightspeedStoreCode),
        lastSyncAt: systems?.lightspeed?.last_sync_at ?? null,
      },
      {
        key: 'xero',
        label: 'Xero',
        icon: <XeroIcon size={16} />,
        connected: Boolean(xeroShortCode),
        lastSyncAt: systems?.xero?.last_sync_at ?? null,
      },
    ]
    if (includeSkimmer) {
      out.push({
        key: 'skimmer',
        label: 'Skimmer',
        icon: <SkimmerIcon size={16} />,
        connected: true,
        lastSyncAt: systems?.skimmer?.last_sync_at ?? null,
      })
    }
    return out
  }, [lightspeedStoreCode, xeroShortCode, fieldManagementSystem, systems])

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        borderColor: 'grey.200',
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          {loading ? (
            <Skeleton variant="text" width={240} height={32} />
          ) : (
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {franchiseName}
            </Typography>
          )}
          {!loading && (country || state) && (
            <Chip
              size="small"
              label={[country, state].filter(Boolean).join(' · ')}
              sx={{ fontWeight: 500, bgcolor: 'grey.100' }}
            />
          )}
        </Stack>

        {!loading && <SystemsIndicator rows={rows} />}
      </Stack>
    </Paper>
  )
}

export default DashboardHero
